//#region Imports
import React, { useState, useEffect, useRef } from 'react';
import { View, StyleSheet, SafeAreaView, TouchableOpacity, Platform, Pressable, FlatList, useWindowDimensions, ActivityIndicator } from 'react-native';
import { Card, Provider } from 'react-native-paper';
import { showMessage } from "react-native-flash-message";
import { useAuthContext } from '../../../hooks/useAuthContext';
import { useTheme, useNavigation } from '@react-navigation/native';
import CustomButton from '../../../components/CustomButton';
import CustomLabel from '../../../components/CustomLabel/CustomLabel';
import CustomInput from '../../../components/CustomInput';
import Modal from "react-native-modal";
import { formatDateForUI } from '../../../constants/FormatDate';
import { useTranslation } from 'react-i18next';
import { useSettingsContext } from '../../../hooks/useSettingsContext';
import { useMyTimesheets } from '../../../hooks/useMyTimesheets';
import { OASYS_CONST } from '../../../constants/const';
//#endregion

const MONTHS = [
    { description: 'January', value: 1 },
    { description: 'February', value: 2 },
    { description: 'March', value: 3 },
    { description: 'April', value: 4 },
    { description: 'May', value: 5 },
    { description: 'June', value: 6 },
    { description: 'July', value: 7 },
    { description: 'August', value: 8 },
    { description: 'September', value: 9 },
    { description: 'October', value: 10 },
    { description: 'November', value: 11 },
    { description: 'December', value: 12 },
];

const TimesheetApprovalScreen = (props) => {
    //#region Dimensions
    const { width, height } = useWindowDimensions();
    const rejectionCommentsModalHeight = height * 30 / 100;
    //#endregion

    //#region Functions definitions, variables and states
    const { colors } = useTheme();
    const navigation = useNavigation();
    const { user, clientApi, username } = useAuthContext();
    const styles = makeStyles(colors, height, width, rejectionCommentsModalHeight);
    const { t, i18n } = useTranslation();
    const { rejectTimesheet, approveTimesheet, loadTSAEmployeeList, loadEmplPendingTSA, loadEmplTSADetails, loadEmplSubmittedTS, loadEmplApprovedTS, loadEmplUnsubmittedTS } = useMyTimesheets();

    const { addApproverComments } = useSettingsContext();
    const [isLoading, setIsLoading] = useState(false);
    const [approveEmplList, setApproveEmplList] = useState([]);
    const [emplTSPendingData, setEmplTSPendingData] = useState([]);
    const [emplTSPendingDetails, setEmplTSPendingDetails] = useState([]);
    const [emplTSAllData, setEmplTSAllData] = useState([]);
    const [emplTSAllDetails, setEmplTSAllDetails] = useState([]);
    const [expandedTS, setExpandedTS] = useState({});
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [typeOfApproval, setTypeOfApproval] = useState('');
    //#endregion

    //#region Fetch Data
    useEffect(() => {
        fetchData();
    }, [])

    async function fetchData() {
        try {
            setIsLoading(true);

            // Load employee list
            const userData = await loadTSAEmployeeList( username);
            setApproveEmplList(userData);
            const timesheetPendingData = {};
            const timesheetPendingDetails = {};
            const timesheetAllData = {};
            const timesheetAllDetails = {};

            await Promise.all(userData.map(async (employee) => {
                const sId = employee.employeeid;

                // Timesheet category loads per employee
                const [
                    pendingDetails,
                    submittedDetails,
                    approvedDetails,
                    unsubmittedDetails
                ] = await Promise.all([
                    loadEmplPendingTSA(  sId),
                    loadEmplSubmittedTS(  sId),
                    loadEmplApprovedTS(  sId),
                    loadEmplUnsubmittedTS(  sId)
                ]);

                timesheetPendingData[sId] = pendingDetails || [];

                timesheetAllData[sId] = [
                    ...(pendingDetails || []),
                    ...(submittedDetails || []),
                    ...(approvedDetails || []),
                    ...(unsubmittedDetails || [])
                ];

                // Load TSA details for pending timesheets in parallel
                const pendingTsPromises = (pendingDetails || []).map(async (ts) => {
                    const tsnoui = ts.tsnoui;
                    if (tsnoui && !timesheetPendingDetails[tsnoui]) {
                        const tsaDetails = await loadEmplTSADetails(  tsnoui);
                        timesheetPendingDetails[tsnoui] = tsaDetails;
                    }
                });
                await Promise.all(pendingTsPromises);
            }));

            // Set states after all data loaded
            setEmplTSPendingData(timesheetPendingData);
            setEmplTSPendingDetails(timesheetPendingDetails);
            setEmplTSAllData(timesheetAllData);
            setEmplTSAllDetails(timesheetAllDetails);
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    }
    //#endregion

    //#region Render Data
    const toggleExpand = (tsnoui) => {
        setExpandedTS(prev => ({
            ...prev,
            [tsnoui]: !prev[tsnoui]
        }));
    };

    const renderTimesheet = (employee, ts, isPending) => {
        // Handle both single tsnoui (string) and combined tsnoui 
        const tsnoui = typeof ts.tsnoui === 'string' ? ts.tsnoui : ts.tsnoui.tsnoUIA || ts.tsnoui.tsnoUIB;
        const isCombinedMonthly = typeof ts.tsnoui === 'object' && ts.tsnoui.tsnoUIA && ts.tsnoui.tsnoUIB;
        const isWeekly = !isCombinedMonthly && ts.Period !== 'A' && ts.Period !== 'B';

        // Get details - for combined monthly, merge both periods
        let details = {};
        if (isCombinedMonthly) {
            const detailsA = emplTSPendingDetails[ts.tsnoui.periodA] || {};
            const detailsB = emplTSPendingDetails[ts.tsnoui.periodB] || {};
            details = {
                chargeableUnits: (detailsA.chargeableUnits || 0) + (detailsB.chargeableUnits || 0),
                nonChargeableUnits: (detailsA.nonChargeableUnits || 0) + (detailsB.nonChargeableUnits || 0),
                payableOvertimeUnits: (detailsA.payableOvertimeUnits || 0) + (detailsB.payableOvertimeUnits || 0),
                expenseTotal: (detailsA.expenseTotal || 0) + (detailsB.expenseTotal || 0)
            };
        } else {
            details = emplTSPendingDetails[tsnoui] || {};
        }
        
        const isExpanded = expandedTS[tsnoui];

        // Determine status label for non-pending timesheets
        const getStatusLabel = () => {
            if (isPending) return null;
            if (ts.Submitted === false) return 'Unsubmitted';
            if (ts.Approved === true) return 'Approved';
            if (ts.Submitted === true) return 'Submitted';
            return 'Unknown';
        };

        return (
            <View key={tsnoui} style={styles.showHide}>
                <Pressable onPress={() => { toggleExpand(tsnoui) }}>
                    <View style={styles.row}>
                        <CustomLabel
                            value={`${ts.Month} ${ts.Year}`}
                            type='AVAILABLEUSED' />
                        <CustomLabel
                            value={isPending ? t('common:period') : t('common:period') + ts.Period}
                            type='AVAILABLEUSED' />
                        {isPending &&
                            <CustomLabel
                                value={ts.Period}
                                type='AVAILABLEUSED' />
                        }
                        <CustomLabel
                            value={employee.name}
                            type='AVAILABLEUSED' />
                        {/* Status button for non-pending */}
                        {!isPending && (
                            <CustomButton
                                text={getStatusLabel()}
                                type={getStatusLabel() === 'Submitted' ? "PENDING" :
                                    (getStatusLabel() === 'Approved' ? "APPROVE" : "CLOSED")}
                                sz={14}
                                show="SHOW"
                                disabled={true}
                                isGradient={getStatusLabel() === 'Approved' ? true : false}
                                style={{ marginLeft: 10, paddingHorizontal: 8, paddingVertical: 4 }}
                            />
                        )}
                    </View>
                </Pressable>
                {/* For pending timesheets if expanded show details for approver */}
                {isExpanded == true && isPending &&
                    <SafeAreaView style={styles.details}>
                        <SafeAreaView style={styles.item}>
                            <View style={styles.lineInvisible} />
                            <View style={styles.row}>
                                <CustomLabel
                                    value={t('common:timesheet') + `:`}
                                    type='TSDETAILSMINOR' />
                                <CustomLabel
                                    value={`${ts.Month} ${ts.Year}`}
                                    type='TSDETAILSMINOR' />
                            </View>
                            <View style={styles.line} />
                            <View style={styles.row}>
                                <CustomLabel
                                    value={t('common:timesheet') + ' ' + t('common:period') + `:`}
                                    type='TSDETAILSMINOR' />
                                <CustomLabel
                                    value={ts.Period}
                                    type='TSDETAILSMINOR' />
                            </View>
                            <View style={styles.line} />
                            <View style={styles.row}>
                                <CustomLabel
                                    value={t('common:chargableU') + `:`}
                                    type='TSDETAILSMINOR' />
                                <CustomLabel
                                    value={details.chargeableUnits ?? 0}
                                    type='TSDETAILSMINOR' />
                            </View>
                            <View style={styles.line} />
                            <View style={styles.row}>
                                <CustomLabel
                                    value={t('common:nonchargableU') + `:`}
                                    type='TSDETAILSMINOR' />
                                <CustomLabel
                                    value={details.nonChargeableUnits ?? 0}
                                    type='TSDETAILSMINOR' />
                            </View>
                            <View style={styles.line} />
                            <View style={styles.row}>
                                <CustomLabel
                                    value={t('common:payableOvertimeU') + `:`}
                                    type='TSDETAILSMINOR' />
                                <CustomLabel
                                    value={details.payableOvertimeUnits ?? 0}
                                    type='TSDETAILSMINOR' />
                            </View>
                            <View style={styles.line} />
                            {!isCombinedMonthly && <>
                                <View style={styles.row}>
                                    <CustomLabel
                                        value={t('common:expenseTotal') + `:`}
                                        type='TSDETAILSMINOR' />
                                    <CustomLabel
                                        value={details.expenseTotal ?? 0}
                                        type='TSDETAILSMINOR' />
                                </View>
                                <View style={styles.line} />
                                <View style={styles.row}>
                                    <CustomLabel
                                        value={t('common:lAttach') + `:`}
                                        type='TSDETAILSMINOR' />
                                    <CustomLabel
                                        value={ts.Attachments > 0 ? ts.Attachments : 'No attachments'}
                                        type='TSDETAILSMINOR' />
                                </View>
                                <View style={styles.line} />
                            </>}
                            {/* Link like label to go view the details page of the timesheet */}
                            <View>
                                <TouchableOpacity onPress={() => { handlePress(ts, isCombinedMonthly, isWeekly) }}>
                                    <CustomLabel
                                        value={t('common:viewTSDetails')}
                                        type="TSDETAILSMINORLINK"
                                    />
                                </TouchableOpacity>
                            </View>
                            <View style={styles.line} />
                        </SafeAreaView>
                        {/* Render accept and reject buttons */}
                        <SafeAreaView>
                            <AcceptRejectButtons
                                item={ts}
                                setIsModalOpen={setIsModalOpen}
                                setTypeOfApproval={setTypeOfApproval} />
                        </SafeAreaView>
                    </SafeAreaView>
                }
                {/* Modal for approve and reject timesheet to add comment */}
                <Modal
                    isVisible={isModalOpen}
                    hasBackdrop={true}
                    backdropColor={colors.background_approved}
                    backdropOpacity={0.9}
                    onBackdropPress={() => setIsModalOpen(false)}
                    onRequestClose={() => setIsModalOpen(false)}>
                    <SafeAreaView
                        style={styles.container}>
                        <StatefulModalContent
                            item={ts}
                            typeOfApproval={typeOfApproval}
                            setIsModalOpen={setIsModalOpen} />
                    </SafeAreaView>
                </Modal>
            </View>
        );
    };

    const StatefulModalContent = ({ item, setIsModalOpen, typeOfApproval }) => {
        const [comments, setComments] = useState();
        const isReject = typeOfApproval === 'reject';

        return (
            <View style={styles.modalContent}>
                {/* TODO: show only for rejection? nothing done with comments on approve */}
                <CustomInput
                    type='LAA_Comments'
                    multiline={true}
                    numberOfLines={Platform.OS === 'android' ? 3 : null}
                    placeholder='Comments (Optional)'
                    value={comments}
                    setValue={setComments}
                />
                <View
                    style={styles.modalButtonsRow}>
                    <CustomButton
                        text={isReject ? t('common:reject') : t('common:approve')}
                        onPress={() => { isReject ? handleReject(item, comments, setIsModalOpen) : handleApprove(item, comments, setIsModalOpen) }}
                        type={isReject ? "REJECT" : "APPROVE"}
                        iconName="close-outline"
                        sz={20}
                        isGradient={true}
                        show="HIDE"
                    />
                    <View style={styles.spacer}></View>
                    <CustomButton
                        onPress={() => setIsModalOpen(false)}
                        type={isReject ? "REJECT" : "APPROVE"}
                        fgColor="#1C5103"
                        text={t('common:cancel')}
                        iconName="checkmark-outline"
                        sz={20}
                        show="HIDE"
                    />
                </View>
            </View>
        );
    };

    const AcceptRejectButtons = ({ item, setIsModalOpen, setTypeOfApproval }) => {
        return (
            <View
                style={{
                    flex: 1,
                    flexDirection: 'row',
                    justifyContent: "center",
                    alignContent: 'center'
                }}>
                {/* Open modal for reject */}
                <CustomButton
                    text={t('common:reject')}
                    onPress={() => { setIsModalOpen(true); setTypeOfApproval('reject'); }}
                    type="REJECT"
                    iconName="close-outline"
                    sz={20}
                    show="HIDE"
                />
                <View style={styles.spacer}></View>
                {/* Open modal for approve */}
                <CustomButton
                    onPress={() => {
                        setIsModalOpen(true);
                        setTypeOfApproval('approve');
                    }} type="APPROVE"
                    text={t('common:approve')}
                    iconName="checkmark-outline"
                    isGradient={true}
                    sz={20}
                    show="HIDE"
                />
            </View>
        )
    };

    // Handle press of link to see timesheet details
    const handlePress = (tsnoUI, isMonthly, isWeekly) => {
        props.setIsMonthlyTimesheet(isMonthly)
        props.setIsWeeklyTimesheet(isWeekly)
        props.selectMyTimesheetDetailsScreen()
        console.log(tsnoUI)
        props.setTSUI(tsnoUI.tsnoui)
        props.setisApprover(true) // set to only view timesheet not edit
    };

    // Render Pending Timesheets on the right
    const renderPendingData = ({ item: employee }) => {
        const timesheets = emplTSPendingData[employee.employeeid] || [];
        if (timesheets.length === 0) {
            return null;
        }
        
        // Check if employee has monthly timesheet context or weekly
        const isMonthlyEmployee = employee.etype === OASYS_CONST.MONTHLY_EMPL_TYPE_TS;
        const isWeeklyEmployee = employee.etype === OASYS_CONST.WEEKLY_EMPL_TYPE_TS;
        
        // If monthly, group timesheets by year-month
        let groupedTimesheets = timesheets;
        if (isMonthlyEmployee) {
            const grouped = {};
            timesheets.forEach(ts => {
                const key = `${ts.Year}-${ts.mnthNumber}`;
                if (!grouped[key]) {
                    grouped[key] = {
                        Year: ts.Year,
                        mnthNumber: ts.mnthNumber,
                        Month: ts.Month,
                        Period: 'A-B',
                        Submitted: ts.Submitted,
                        Approved: ts.Approved,
                        Attachments: ts.Attachments,
                        isMonthly: true,
                        periodA_tsnoui: ts.Period === 'A' ? ts.tsnoui : null,
                        periodB_tsnoui: ts.Period === 'B' ? ts.tsnoui : null,
                    };
                } else {
                    // Update combined entry with both period tsnouis
                    if (ts.Period === 'A') {
                        grouped[key].periodA_tsnoui = ts.tsnoui;
                    } else if (ts.Period === 'B') {
                        grouped[key].periodB_tsnoui = ts.tsnoui;
                    }
                    grouped[key].Submitted = grouped[key].Submitted && ts.Submitted;
                    grouped[key].Approved = grouped[key].Approved && ts.Approved;
                    grouped[key].Attachments += ts.Attachments;
                }
            });
            
            // Convert to final format with combined tsnoui object
            groupedTimesheets = Object.values(grouped).map(ts => ({
                ...ts,
                tsnoui: {
                    tsnoUIA: ts.periodA_tsnoui,
                    tsnoUIB: ts.periodB_tsnoui
                }
            }));
        } else  if (isWeeklyEmployee) {
            // For weekly employees, format the period display with week dates
            groupedTimesheets = timesheets.map(ts => ({
                ...ts,
                Period: `${formatDateForUI(ts.weekStart)}-${formatDateForUI(ts.weekEnd)}`,
                isWeekly: true
            }));
        }
        
        return renderEmployee(employee, groupedTimesheets, true);
    }

    // Render all states timesheets on the left
    const renderAllData = ({ item: employee }) => {
        const timesheets = emplTSAllData[employee.employeeid] || [];
        if (timesheets.length === 0) {
            return null;
        }
        
        // Check if employee has monthly timesheet context
        const isMonthlyEmployee = employee.etype === OASYS_CONST.MONTHLY_EMPL_TYPE_TS;
        const isWeeklyEmployee = employee.etype === OASYS_CONST.WEEKLY_EMPL_TYPE_TS;

        // If monthly, group timesheets by year-month
        let groupedTimesheets = timesheets;
        if (isMonthlyEmployee) {
            const grouped = {};
            timesheets.forEach(ts => {
                const key = `${ts.Year}-${ts.mnthNumber}`;
                if (!grouped[key]) {
                    grouped[key] = {
                        Year: ts.Year,
                        mnthNumber: ts.mnthNumber,
                        Month: ts.Month,
                        tsnoui: ts.tsnoui, // Use first period's tsnoui
                        Period: 'A-B', // Combined periods
                        Submitted: ts.Submitted,
                        Approved: ts.Approved,
                        Attachments: ts.Attachments,
                        isMonthly: true
                    };
                } else {
                    // Update combined entry with latest status
                    grouped[key].Submitted = grouped[key].Submitted && ts.Submitted;
                    grouped[key].Approved = grouped[key].Approved && ts.Approved;
                    grouped[key].Attachments += ts.Attachments;
                }
            });
            groupedTimesheets = Object.values(grouped);
        } else  if (isWeeklyEmployee) {
            // For weekly employees, format the period display with week dates
            groupedTimesheets = timesheets.map(ts => ({
                ...ts,
                Period: `${formatDateForUI(ts.weekStart)}-${formatDateForUI(ts.weekEnd)}`,
                isWeekly: true
            }));
        }
        
        return renderEmployee(employee, groupedTimesheets, false);
        
    }

    // Render each timesheet of the employee provided
    const renderEmployee = (employee, timesheets, isPending) => {
        return (
            <Provider>
                {timesheets.map(ts => (
                    <Provider key={ts.tsnoui}>
                        <Card style={isPending ? styles.cardstyleOpen : styles.cardstyle}>
                            <Card.Content>
                                {renderTimesheet(employee, ts, isPending)}
                            </Card.Content>
                        </Card>
                    </Provider>
                ))}
            </Provider>
        );
    };
    //#endregion

    //#region Handle Actions
    const handleApprove = async (item, comments, isModalOpen) => {
        const isMonthlyTsnoui = item.Period === 'A-B';
        
        if(isMonthlyTsnoui){
            console.log(item.tsnoui.periodA )
            const apprA = await approveTimesheet(  item.tsnoui.tsnoUIA, comments ? comments : "")
            const apprB = await approveTimesheet(  item.tsnoui.tsnoUIB, comments ? comments : "")
        
            if(apprA.statusCode === 200 && apprB.statusCode === 200){
                showMessage({
                    message: t("common:rejectSuccess"),
                    type: "success",
                    duration: 3000,
                });
                fetchData();
                isModalOpen(false)
            } else {
                showMessage({
                    message: t("common:errorSave"),
                    type: "danger",
                    duration: 3000,
                });
            }
        } else {
            approveTimesheet(  item.tsnoui, comments ? comments : "").then((res) => {
                if (res.statusCode === 200)
                    showMessage({
                        message: t("common:approvedSuccess"),
                        type: "success",
                        duration: 3000,
                    });
                // rehresh on success
                fetchData();
                isModalOpen(false)
            })
                .catch((error) => {
                    showMessage({
                        message: t("common:errorSave"),
                        type: "danger",
                        duration: 3000,
                    });
                })
        }
    };

    const handleReject = async (item, comments, isModalOpen) => {
        const isMonthlyTsnoui = item.Period === 'A-B';
        
        if(isMonthlyTsnoui){
            const rejA = await rejectTimesheet(  item.tsnoui.tsnoUIA, comments ? comments : "")
            const rejB = await rejectTimesheet(  item.tsnoui.tsnoUIB, comments ? comments : "")
        
            if(rejA.statusCode === 200 && rejB.statusCode === 200){
                isModalOpen(false)
                showMessage({
                    message: t("common:rejectSuccess"),
                    type: "success",
                    duration: 3000,
                });
                fetchData();
            } else {
                isModalOpen(false)
                showMessage({
                    message: t("common:errorReject"),
                    type: "danger",
                    duration: 3000,
                });
            }
        } else {
            rejectTimesheet(  item.tsnoui, comments ? comments : "").then((res) => {
                if (res.statusCode === 200){
                    showMessage({
                        message: t("common:rejectSuccess"),
                        type: "success",
                        duration: 3000,
                    });
                    fetchData();
                    isModalOpen(false)
                }
            })
                .catch((error) => {
                    showMessage({
                        message: t("common:errorReject"),
                        type: "danger",
                        duration: 3000,
                    });
                })
        }
        
    };
    //#endregion

    return (
        <SafeAreaView style={styles.main}>
            <SafeAreaView style={styles.rowView}>
                {(Platform.OS === 'web' && width > 1200) &&
                    <SafeAreaView style={styles.subMain}>
                        <CustomLabel
                            value={t('navigate:TSA')}
                            type='MINORTITLE'
                        />
                        {isLoading ?
                            <View style={styles.loader}>
                                <ActivityIndicator animating={true} color={colors.blue} />
                            </View>
                            :
                            <FlatList
                                data={approveEmplList}
                                keyExtractor={item => item.employeeid}
                                renderItem={renderAllData}
                                ListEmptyComponent={
                                    <CustomLabel value={t('common:nopreviousTS')} type="SUBTITLE" />
                                }
                                contentContainerStyle={{ padding: 12 }}
                            />
                        }
                    </SafeAreaView>
                }
                <SafeAreaView style={styles.subMain}>
                    {isLoading ?
                        <View style={styles.loader}>
                            <ActivityIndicator animating={true} color={colors.blue} />
                        </View>
                        :
                        <>
                            <CustomLabel
                                value={t('common:pendingApprovalsTS')}
                                type="MINORTITLE"
                                style={{ marginBottom: 8, alignContent: 'center' }}
                            />
                            <View style={{ borderBottomColor: colors.buttons_primary, borderBottomWidth: StyleSheet.hairlineWidth, marginBottom: 12 }} />
                            <FlatList
                                data={approveEmplList}
                                keyExtractor={item => item.employeeid}
                                renderItem={renderPendingData}
                                ListEmptyComponent={
                                    <CustomLabel value={t('common:nopendingTS')} type="SUBTITLE" />
                                }
                                contentContainerStyle={{ position: 'fixed', width: '100%', padding: 12 }}
                            />
                        </>
                    }
                </SafeAreaView>
            </SafeAreaView>
        </SafeAreaView>
    )
}

const makeStyles = (colors, height, width, rejectionCommentsModalHeight) => StyleSheet.create({
    main: {
        flex: 1,
        backgroundColor: colors.background_card,
        borderRadius: 35,
        height: "100%",
        elevation: Platform.OS === "web" ? 3 : 0,
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.1,
        shadowRadius: Platform.OS === "web" ? 5 : 0,
    },
    details: {
        flex: 1,
        margin: '2%',
    },
    subMain: {
        flex: 1,
        marginTop: 10,
    },
    rowView: {
        flex: 1,
        flexDirection: 'row',
        justifyContent: 'center',
    },
    container: {
        justifyContent: 'center',
        alignItems: 'center',
    },
    spacer: {
        margin: '2%'
    },
    cardstyle: {
        marginLeft: (Platform.OS === 'web' && width > 1200 ? '2%' : '4%'),
        marginRight: (Platform.OS === 'web' && width > 1200 ? '2%' : '4%'),
        marginTop: 10,
        marginBottom: 10,
        borderRadius: 5,
        elevation: 3,
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.1,
        shadowRadius: 5,
        backgroundColor: colors.background_card,
        flex: 'none',
    },
    cardstyleOpen: {
        marginLeft: (Platform.OS === 'web' && width > 1200 ? '2%' : '4%'),
        marginRight: (Platform.OS === 'web' && width > 1200 ? '2%' : '4%'),
        marginTop: 10,
        marginBottom: 10,
        borderRadius: 5,
        borderWidth: 1,
        borderColor: colors.buttons_primary,
        elevation: 3,
        shadowColor: colors.card_line,
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.1,
        shadowRadius: 5,
        backgroundColor: colors.background_card,
        flex: 'none',
    },
    showHide: {
        borderRadius: 35,
        backgroundColor: colors.background_card,
    },
    line: {
        borderBottomColor: colors.buttons_primary,
        borderBottomWidth: StyleSheet.hairlineWidth,
        marginTop: (Platform.OS === 'web' && width > 1200) ? 10 : 20,
        marginBottom: (Platform.OS === 'web' && width > 1200) ? 10 : 20,
    },
    lineInvisible: {
        borderBottomColor: 'transparent',
        borderBottomWidth: StyleSheet.hairlineWidth,
        marginTop: (Platform.OS === 'web' && width > 1200) ? 0 : 20,
        marginBottom: (Platform.OS === 'web' && width > 1200) ? 0 : 20,
    },
    row: {
        flex: 1,
        flexDirection: "row",
    },
    item: {
        flex: 1,
        padding: 10,
    },
    modalContent: {
        width: (Platform.OS === 'web' && width > 1200 ? '40%' : '100%'),
        height: rejectionCommentsModalHeight,
        justifyContent: 'center',
        backgroundColor: colors.background_card,
        borderRadius: 20,
        borderWidth: 3,
        borderColor: '#a1a1a1',
        padding: 10,
    },
    modalButtonsRow: {
        flexDirection: 'row',
        alignSelf: 'center',
        marginTop: 10,
        width: '90%',
        flex: 1,
    },
    loading: {
        flex: 1,
        marginTop: "1%",
        marginLeft: "2%",
        marginRight: "2%",
    },
});

export default TimesheetApprovalScreen;